import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@/common/styles/style";
import { ChangeEvent, useEffect, useState } from "react";
import useCheckbox from "@/hooks/useCheckbox";
import SearchBox from "@/molecules/searchBox";
import { styles } from "./style";
import Dialog from "@/molecules/dialog";
import { CollectedDataType, apiMachineType } from "@/common/type/type";

interface TrendModifyProps {
  open: boolean;
  selectedMachine: apiMachineType;
  onSave: (data: CollectedDataType[]) => void;
  onClose: () => void;
  selectedNames?: string[];
}

export default function TrendModify({
  open,
  selectedMachine,
  onSave,
  onClose,
  selectedNames,
}: TrendModifyProps) {
  const [filteredData, setFilteredData] = useState<CollectedDataType[]>([]);
  const {
    checkedItems,
    setCheckedItems,
    handleCheckboxChange,
    resetCheckboxes,
  } = useCheckbox(filteredData.length || 0);
  const [checkedCount, setCheckedCount] = useState(0);
  const MAX_CHECKED = 5;
  const [search, setSearch] = useState({ key: "", value: "" });
  const [data, setData] = useState<CollectedDataType[]>([]);

  useEffect(() => {
    if (filteredData.length > 0 && selectedNames) {
      const newCheckedState = filteredData.map((data) =>
        selectedNames.includes(data.dataName)
      );
      setCheckedItems(newCheckedState);

      const selectedData = filteredData.filter((data) =>
        selectedNames.includes(data.dataName)
      );
      setData(selectedData);
    }
  }, [filteredData, selectedNames, setCheckedItems]);

  useEffect(() => {
    if (!open) {
      resetCheckboxes();
      setCheckedCount(0);
      setData([]);
    } else {
      const checkServerConnection = async () => {
        try {
          const response = await fetch(`/api/dashboard/widget/item/${selectedMachine.id}`, {
              method: "GET",
              credentials: "include",
          });

          const data = await response.json();
          if (response.ok) {
            console.log(data.message);
            setFilteredData(data.result);
          } else {
            console.error("サーバ接続エラー: ", response.statusText);
          }
        } catch (error) {
          console.error("サーバ接続エラー:", error);
        }
      };

      checkServerConnection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedMachine.id]);

  const handleCheckboxChangeWithLimit = (
    index: number,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = event.target.checked;

    if (checkedCount < MAX_CHECKED || !isChecked) {
      handleCheckboxChange(index, event);
      setCheckedCount((prevCount) => prevCount + (isChecked ? 1 : -1));
      const selectedItem = filteredData[index];
      setData((prevData) =>
        isChecked
          ? [...prevData, selectedItem]
          : prevData.filter((item) => item.dataName !== selectedItem.dataName)
      );
    }
  };

  const handleSearch = (searchType: string, value: string) => {
    setSearch((prevSearch) => ({
      ...prevSearch,
      [searchType]: value,
    }));
  };

  const handleSave = async () => {
    const viewData = data;
    onSave(viewData);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="トレンド表示編集"
      sx={styles.container}
      contentChildren={
        <>
          <SearchBox onSearch={(value) => handleSearch("key", value)} />
          <Table sx={styles.table}>
            <TableHead>
              <TableRow sx={styles.tableRow}>
                <TableCell sx={styles.tableCell}>
                  <Checkbox disabled />
                </TableCell>
                <TableCell sx={styles.tableCell}>変数</TableCell>
                <TableCell sx={styles.tableCell}>値</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData
                .filter((data) =>
                  data.dataName.toLowerCase().includes(search.key.toLowerCase())
                )
                .map((data, index) => (
                  <TableRow sx={styles.tableRow} key={`trendMod-${index}`}>
                    <TableCell width="20%" sx={styles.tableCell}>
                      <Checkbox
                        checked={checkedItems[index] ?? false}
                        onChange={(e) =>
                          handleCheckboxChangeWithLimit(index, e)
                        }
                        disabled={
                          checkedCount >= MAX_CHECKED &&
                          !(checkedItems[index] ?? false)
                        }
                      />
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      <Typography>{data.dataName}</Typography>
                    </TableCell>
                    <TableCell sx={styles.tableCell}>
                      <Typography>{data.value}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </>
      }
      actionsChildren={
        <Button variant="contained">
          <Typography variant="button" onClick={handleSave}>
            保存
          </Typography>
        </Button>
      }
    />
  );
}
